import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoSearch } from "react-icons/io5";
import { quizSearchDecks, quizGetDeckFilters } from "./quizApiService";
import {
  resolveVisibleCategories,
  parseFiltersResponse,
  matchesSchoolGradeFilter,
  getPublicFallbackCategories,
  collectFiltersFromDecks,
  PUBLIC_FALLBACK_GRADES,
} from "./zubrotekaQuickFilterConfig";
import DeckCard, { DeckCardSkeleton } from "../../components/DeckCard";
import ZubrotekaQuickFilters from "../../components/quiz/ZubrotekaQuickFilters";
import Button from "../../components/ui/Button";

const HIDDEN_STATUSES = ["DELETED", "INACTIVE", "REMOVED", "ARCHIVED", "DISABLED", "HIDDEN"];
const PAGE_SIZE = 8;
const MIN_VISIBLE_WITH_GRADE = 8;

export default function QuizHome() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSubject = searchParams.get("subject") || "";

  const [search, setSearch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState(urlSubject || null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [categories, setCategories] = useState([]);
  const [grades, setGrades] = useState([]);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastId, setLastId] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const observer = useRef(null);
  const isFirstRender = useRef(true);
  const fetchGenerationRef = useRef(0);
  const lastIdRef = useRef("");
  const hasMoreRef = useRef(true);
  const selectedGradeRef = useRef(null);
  const selectedSubjectRef = useRef(selectedSubject);
  const searchRef = useRef(search);

  useEffect(() => {
    lastIdRef.current = lastId;
  }, [lastId]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    selectedGradeRef.current = selectedGrade;
  }, [selectedGrade]);

  useEffect(() => {
    selectedSubjectRef.current = selectedSubject;
  }, [selectedSubject]);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Anonymous users get 403 on get-filters — fall back to deck sample / defaults.
      try {
        const response = await quizGetDeckFilters({ countries: ["LT"] });
        if (cancelled) return;
        const { subjects, grades: apiGrades } = parseFiltersResponse(response);
        const visible = resolveVisibleCategories(subjects);
        if (visible.length > 0 || apiGrades.length > 0) {
          setCategories(visible.length > 0 ? visible : getPublicFallbackCategories());
          setGrades(apiGrades.length > 0 ? apiGrades : PUBLIC_FALLBACK_GRADES);
          return;
        }
      } catch (err) {
        console.warn("Quiz filters API unavailable, using public fallback:", err?.response?.status || err?.message);
      }

      try {
        const sample = await quizSearchDecks({ pageSize: 50, search: "", lastId: "" });
        if (cancelled) return;
        const { subjects, grades: deckGrades } = collectFiltersFromDecks(sample?.data || []);
        const visible = resolveVisibleCategories(subjects);
        setCategories(visible.length > 0 ? visible : getPublicFallbackCategories());
        setGrades(deckGrades.length > 0 ? deckGrades : PUBLIC_FALLBACK_GRADES);
      } catch (err) {
        console.error("Quiz filter fallback error:", err);
        if (!cancelled) {
          setCategories(getPublicFallbackCategories());
          setGrades(PUBLIC_FALLBACK_GRADES);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep local subject in sync when URL changes externally
  useEffect(() => {
    setSelectedSubject(urlSubject || null);
    if (urlSubject) {
      setSelectedGrade(null);
    }
  }, [urlSubject]);

  const syncSubjectToUrl = useCallback(
    (subject) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (subject) {
            next.set("subject", subject);
          } else {
            next.delete("subject");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const filterHiddenDecks = (rawDecks) =>
    rawDecks.filter((deck) => {
      const status = deck?.status?.toUpperCase();
      return !HIDDEN_STATUSES.includes(status);
    });

  const fetchDecks = useCallback(async (reset = false) => {
    if (!reset && !hasMoreRef.current) return;

    const generation = reset
      ? ++fetchGenerationRef.current
      : fetchGenerationRef.current;

    setLoading(true);
    setError(null);

    try {
      let currentLastId = reset ? "" : lastIdRef.current;
      let currentHasMore = true;
      let accumulated = reset ? [] : null;
      let guard = 0;

      // When a grade filter is active, keep paging until we have enough
      // visible decks or there are no more pages (avoids empty-looking grids).
      while (guard < 20) {
        guard += 1;

        const response = await quizSearchDecks({
          search: searchRef.current || "",
          lastId: currentLastId,
          pageSize: PAGE_SIZE,
          mainSubject: selectedSubjectRef.current || null,
        });

        if (generation !== fetchGenerationRef.current) return;

        const rawDecks = response?.data || [];
        const pageDecks = filterHiddenDecks(rawDecks);
        currentHasMore = rawDecks.length > 0;
        currentLastId =
          rawDecks.length > 0 ? rawDecks[rawDecks.length - 1]._id : currentLastId;

        if (accumulated) {
          accumulated = [...accumulated, ...pageDecks];
        } else {
          setDecks((prev) => [...prev, ...pageDecks]);
        }

        const grade = selectedGradeRef.current;
        const visibleCount = grade
          ? (accumulated || []).filter((deck) =>
              matchesSchoolGradeFilter(deck.schoolGrades, grade)
            ).length
          : (accumulated || []).length;

        const needMoreForGrade =
          Boolean(grade) &&
          accumulated &&
          visibleCount < MIN_VISIBLE_WITH_GRADE &&
          currentHasMore;

        if (!needMoreForGrade || !reset) break;
      }

      if (generation !== fetchGenerationRef.current) return;

      if (accumulated) {
        setDecks(accumulated);
      }

      setLastId(currentLastId);
      setHasMore(currentHasMore);
      lastIdRef.current = currentLastId;
      hasMoreRef.current = currentHasMore;
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return;
      console.error("Quiz deck fetch error:", err);
      setError(err?.response?.data?.error || err?.message || "Failed to load decks");
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchDecks(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, selectedGrade]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const debounce = setTimeout(() => {
      fetchDecks(true);
    }, 500);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (loading || !hasMore) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchDecks(false);
        }
      },
      { threshold: 1.0 }
    );

    const trigger = document.getElementById("quizLoadMoreTrigger");
    if (trigger) observer.current.observe(trigger);

    return () => observer.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, selectedGrade]);

  const visibleDecks = useMemo(() => {
    if (!selectedGrade) return decks;
    return decks.filter((deck) =>
      matchesSchoolGradeFilter(deck.schoolGrades, selectedGrade)
    );
  }, [decks, selectedGrade]);

  const handleSubjectSelect = (category) => {
    const nextSubject = category.resolvedSubjects[0] || null;
    const isSame = selectedSubject && category.resolvedSubjects.includes(selectedSubject);

    if (isSame) {
      setSelectedSubject(null);
      setSelectedGrade(null);
      syncSubjectToUrl(null);
      return;
    }

    setSelectedSubject(nextSubject);
    setSelectedGrade(null);
    syncSubjectToUrl(nextSubject);
  };

  const handleGradeSelect = (grade) => {
    if (selectedGrade === grade) {
      setSelectedGrade(null);
      return;
    }

    setSelectedGrade(grade);
    setSelectedSubject(null);
    syncSubjectToUrl(null);
  };

  return (
    <div className="flex-1 w-full">
      <div className="px-10 py-8 max-w-[1500px] mx-auto md:px-5">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-darkblue"></span>
            <span className="text-sm font-semibold text-darkblue uppercase tracking-wide">
              {t("quiz.library")}
            </span>
          </div>
          <h1 className="text-[32px] font-bold text-black leading-tight">
            {t("quiz.chooseDeckTitle")}
          </h1>
          <p className="text-lightgray mt-1 text-base">
            {visibleDecks.length} {t("quiz.decksForDisciplines")}
          </p>
        </div>

        {/* Search */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="relative w-[280px] md:w-full">
            <IoSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-lightgray text-lg" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="..."
              className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:border-darkblue"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lightgray text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              /
            </span>
          </div>
        </div>

        <ZubrotekaQuickFilters
          categories={categories}
          grades={grades}
          selectedSubject={selectedSubject}
          selectedGrade={selectedGrade}
          onSubjectSelect={handleSubjectSelect}
          onGradeSelect={handleGradeSelect}
        />

        {/* Deck grid */}
        {error && (
          <div className="text-center py-16">
            <p className="text-lg text-red-500 mb-4">{error}</p>
            <Button variant="primary" size="lg" onClick={() => fetchDecks(true)}>
              {t("retry") || "Retry"}
            </Button>
          </div>
        )}

        {!error && visibleDecks.length === 0 && !loading && (
          <p className="text-center text-gray-400 text-lg py-16">No decks found</p>
        )}

        {!error && loading && decks.length === 0 && (
          <div className="grid gap-5 grid-cols-4 lg:grid-cols-3 md:grid-cols-2 sm:!grid-cols-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <DeckCardSkeleton key={i} />
            ))}
          </div>
        )}

        {!error && visibleDecks.length > 0 && (
          <div className="grid gap-5 grid-cols-4 lg:grid-cols-3 md:grid-cols-2 sm:!grid-cols-1">
            {visibleDecks.map((item) => (
              <DeckCard
                key={item.deckId || item._id}
                deck={item}
                to={`/quiz/deck/${item.deckId || item._id}`}
                showLanguage={Boolean(item?.language)}
              />
            ))}
          </div>
        )}

        {loading && decks.length > 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div className="w-10 h-10 border-[3px] border-darkblue border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-400">Loading more...</span>
          </div>
        )}

        {hasMore && !error && <div id="quizLoadMoreTrigger" className="h-10"></div>}
      </div>
    </div>
  );
}
